import React, { useEffect, useState, useRef, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, FileUp, FileCheck, Camera, X, Download, CheckCircle, XCircle, Send, Eye, Clock, CreditCard, ShieldCheck, ArrowRight, Trash2 } from 'lucide-react';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const CATEGORIES = [
  'Travel Allowance (TA/DA)', 'Office Supplies', 'Printing & Stationery',
  'Communication Expenses', 'Meeting Expenses', 'Training Expenses',
  'Medical Reimbursement', 'Conveyance', 'Office Rent', 'Utilities',
  'Honorarium', 'Miscellaneous', 'Other',
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock },
  verified: { label: 'Verified', color: 'bg-blue-100 text-blue-800 border-blue-300', icon: ShieldCheck },
  approved: { label: 'Approved', color: 'bg-purple-100 text-purple-800 border-purple-300', icon: CheckCircle },
  paid: { label: 'Paid', color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CreditCard },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800 border-red-300', icon: XCircle },
};

const Requisitions = () => {
  const { user, isAdmin } = useAuth();
  const role = user?.role;
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState({ open: false, req: null, action: '' });
  const [summaryDialog, setSummaryDialog] = useState({ open: false, req: null });
  const [remarks, setRemarks] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [billFile, setBillFile] = useState(null);
  const [billMode, setBillMode] = useState('upload');
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const billInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [formData, setFormData] = useState({
    category: CATEGORIES[0],
    amount: 0,
    description: '',
    date: new Date().toISOString().split('T')[0],
  });

  const fetchRequisitions = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await api.get('/requisitions', { params });
      setRequisitions(r.data);
    } catch { if (!silent) toast.error('Failed to fetch requisitions'); }
    finally { if (!silent) setLoading(false); }
  };

  useEffect(() => { fetchRequisitions(); }, [statusFilter]);

  // Real-time auto-refresh (silent — no spinner / toast)
  useAutoRefresh(() => fetchRequisitions(true));

  const startCamera = useCallback(async () => {
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 960 } } });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      streamRef.current = stream;
      setCameraActive(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 150);
    } catch (err) {
      console.error('Camera error:', err);
      toast.error('Unable to access camera. Please use file upload instead.');
      setBillMode('upload');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraActive(false);
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    const dataUrl = c.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();
    fetch(dataUrl).then(r => r.blob()).then(blob => setBillFile(new File([blob], `bill_${Date.now()}.jpg`, { type: 'image/jpeg' })));
  }, [stopCamera]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!billFile) { toast.error('Bill/Voucher is mandatory'); return; }
    try {
      const fd = new FormData();
      fd.append('data', JSON.stringify({ ...formData, date: new Date(formData.date).toISOString() }));
      fd.append('bill_file', billFile);
      await api.post('/requisitions', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Requisition submitted successfully');
      closeDialog();
      fetchRequisitions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  const handleAction = async () => {
    const { req, action } = actionDialog;
    if (!req) return;
    try {
      await api.put(`/requisitions/${req.id}/${action}`, { remarks });
      toast.success(`Requisition ${action === 'pay' ? 'marked as paid' : action + 'd'} successfully`);
      setActionDialog({ open: false, req: null, action: '' });
      setRemarks('');
      fetchRequisitions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const handleDownloadBill = async (reqId, filename) => {
    try {
      const r = await api.get(`/requisitions/${reqId}/bill`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url; a.setAttribute('download', filename || 'bill'); document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download bill'); }
  };

  const closeDialog = () => {
    stopCamera(); setDialogOpen(false); setBillFile(null); setCapturedImage(null); setBillMode('upload');
    if (billInputRef.current) billInputRef.current.value = '';
    setFormData({ category: CATEGORIES[0], amount: 0, description: '', date: new Date().toISOString().split('T')[0] });
  };

  const canCreate = ['Branch', 'Admin', 'Checker'].includes(role);
  const canVerify = ['Checker', 'Admin'].includes(role);
  const canApprove = role === 'Admin';
  const canPay = ['Account', 'Admin'].includes(role);
  const canReject = ['Checker', 'Admin'].includes(role);
  const canDelete = user?.email === 'hq@wwags.org';

  const handleDelete = async (req) => {
    if (!window.confirm(`Delete requisition ${req.requisition_no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/requisitions/${req.id}`);
      toast.success(`Requisition ${req.requisition_no} deleted`);
      fetchRequisitions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const getActionButtons = (req) => {
    const btns = [];
    if (req.status === 'pending' && canVerify)
      btns.push({ label: 'Verify', action: 'verify', color: 'bg-blue-600 hover:bg-blue-700 text-white', icon: ShieldCheck });
    if (req.status === 'verified' && canApprove)
      btns.push({ label: 'Approve', action: 'approve', color: 'bg-purple-600 hover:bg-purple-700 text-white', icon: CheckCircle });
    if (req.status === 'approved' && canPay)
      btns.push({ label: 'Mark Paid', action: 'pay', color: 'bg-emerald-600 hover:bg-emerald-700 text-white', icon: CreditCard });
    if (!['paid', 'rejected'].includes(req.status) && canReject)
      btns.push({ label: 'Reject', action: 'reject', color: 'bg-red-600 hover:bg-red-700 text-white', icon: XCircle });
    return btns;
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="requisitions-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Requisitions</h1>
          <p className="text-sm text-slate-600">Reimbursement requests & approval workflow</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const r = await api.get('/requisitions/template/download', { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement('a'); a.href = url;
                a.setAttribute('download', `WWAGS_Requisition_Form.xlsx`);
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Template downloaded');
              } catch { toast.error('Failed to download template'); }
            }}
            className="bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-1" />Requisition Form
          </Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); else setDialogOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="new-requisition-btn">
                  <Plus className="w-4 h-4 mr-2" />New Requisition
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Submit Requisition</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={formData.category} onValueChange={v => setFormData({ ...formData, category: v })}>
                        <SelectTrigger data-testid="req-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Amount (Rs.) *</Label>
                      <Input type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: parseFloat(e.target.value) })} required data-testid="req-amount" />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Description *</Label>
                      <Input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} required data-testid="req-description" />
                    </div>
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input type="date" value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} required />
                    </div>
                  </div>

                  {/* Bill Upload */}
                  <div className="space-y-2">
                    <Label>Bill / Voucher <span className="text-red-600 font-bold">*</span></Label>
                    <div className="flex gap-1 mb-2">
                      <button type="button" onClick={() => { setBillMode('upload'); stopCamera(); setCapturedImage(null); }} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-lg border transition-colors ${billMode === 'upload' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`} data-testid="req-bill-upload-tab"><FileUp className="w-3.5 h-3.5" /> Upload File</button>
                      <button type="button" onClick={() => { setBillMode('camera'); setBillFile(null); startCamera(); }} className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-medium rounded-lg border transition-colors ${billMode === 'camera' ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`} data-testid="req-bill-camera-tab"><Camera className="w-3.5 h-3.5" /> Capture</button>
                    </div>
                    {billMode === 'upload' && (
                      <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${billFile ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400'}`} onClick={() => billInputRef.current?.click()} data-testid="req-bill-upload-area">
                        <input type="file" ref={billInputRef} onChange={e => { if (e.target.files[0]) { setBillFile(e.target.files[0]); setCapturedImage(null); } }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" className="hidden" />
                        {billFile && !capturedImage ? (
                          <div className="flex items-center justify-center gap-2 text-emerald-700"><FileCheck className="w-5 h-5" /><span className="text-sm font-medium">{billFile.name}</span></div>
                        ) : (
                          <div className="text-slate-500"><FileUp className="w-8 h-8 mx-auto mb-1 text-slate-400" /><p className="text-sm">Click to upload</p><p className="text-xs text-slate-400">PDF, JPG, PNG, DOC, XLS</p></div>
                        )}
                      </div>
                    )}
                    {billMode === 'camera' && (
                      <div className="rounded-lg border-2 border-slate-300 overflow-hidden bg-black" data-testid="req-camera-area">
                        <canvas ref={canvasRef} className="hidden" />
                        {capturedImage ? (
                          <div className="relative">
                            <img src={capturedImage} alt="Bill" className="w-full max-h-48 object-contain bg-slate-900" />
                            <div className="bg-emerald-50 border-t border-emerald-200 p-2 flex justify-between items-center">
                              <span className="text-xs text-emerald-700 flex items-center gap-1"><FileCheck className="w-3 h-3" /> Photo captured</span>
                              <Button type="button" size="sm" variant="outline" onClick={() => { setCapturedImage(null); setBillFile(null); startCamera(); }} className="h-7 text-xs"><Camera className="w-3 h-3 mr-1" /> Retake</Button>
                            </div>
                          </div>
                        ) : cameraActive ? (
                          <div>
                            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-48 object-contain bg-slate-900" />
                            <div className="bg-slate-800 p-2 flex justify-center">
                              <button type="button" onClick={capturePhoto} className="w-12 h-12 rounded-full border-4 border-white bg-red-500 hover:bg-red-600 flex items-center justify-center" data-testid="req-capture-btn"><Camera className="w-5 h-5 text-white" /></button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-6 text-center text-slate-400"><Camera className="w-8 h-8 mx-auto mb-1" /><p className="text-sm">Starting camera...</p></div>
                        )}
                      </div>
                    )}
                    {!billFile && <p className="text-xs text-red-500">Bill/Voucher is mandatory</p>}
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={closeDialog}>Cancel</Button>
                    <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="submit-requisition-btn"><Send className="w-4 h-4 mr-2" />Submit Requisition</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Workflow Legend */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2 text-xs">
        <span className="font-semibold text-slate-600">Workflow:</span>
        <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Mentor Submits</span>
        <ArrowRight className="w-3 h-3 text-slate-400" />
        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Checker Verifies</span>
        <ArrowRight className="w-3 h-3 text-slate-400" />
        <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Admin Approves</span>
        <ArrowRight className="w-3 h-3 text-slate-400" />
        <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">Account Pays</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>
      ) : requisitions.length === 0 ? (
        <div className="text-center py-12 text-slate-500"><Send className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>No requisitions found</p></div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Req. No.</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Date</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Branch</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Submitted By</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Category</th>
                  <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600">Description</th>
                  <th className="px-3 py-3 text-right text-xs font-semibold text-slate-600">Amount</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600">Bill</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600">Status</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requisitions.map(req => {
                  const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
                  const Icon = sc.icon;
                  return (
                    <tr key={req.id} className="hover:bg-slate-50" data-testid={`req-row-${req.id}`}>
                      <td className="px-3 py-3 font-medium">
                        <button
                          onClick={() => setSummaryDialog({ open: true, req })}
                          className="text-blue-700 hover:text-blue-900 hover:underline font-medium cursor-pointer"
                          data-testid={`req-link-${req.id}`}
                        >
                          {req.requisition_no}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-slate-600">{new Date(req.date).toLocaleDateString('en-IN')}</td>
                      <td className="px-3 py-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{req.branch_name || '-'}</span></td>
                      <td className="px-3 py-3 text-slate-600 text-xs">{req.submitted_by_email}</td>
                      <td className="px-3 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">{req.category}</span></td>
                      <td className="px-3 py-3 text-slate-700 text-xs max-w-[200px] truncate">{req.description}</td>
                      <td className="px-3 py-3 text-right font-bold text-slate-900">Rs.{req.amount?.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-3 text-center">
                        {req.has_bill ? (
                          <Button variant="ghost" size="sm" onClick={() => handleDownloadBill(req.id, req.bill_filename)} className="text-emerald-600 hover:bg-emerald-50 h-7 px-2" data-testid={`download-req-bill-${req.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Bill</span>
                          </Button>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border font-semibold ${sc.color}`}>
                          <Icon className="w-3 h-3" />{sc.label}
                        </span>
                        {req.status === 'verified' && req.verified_by_email && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Verified by {req.verified_by_email}{req.verified_at ? ` on ${new Date(req.verified_at).toLocaleDateString('en-IN')}` : ''}</p>
                        )}
                        {req.status === 'approved' && req.approved_by_email && (
                          <p className="text-[10px] text-slate-400 mt-0.5">Approved by {req.approved_by_email}{req.approved_at ? ` on ${new Date(req.approved_at).toLocaleDateString('en-IN')}` : ''}</p>
                        )}
                        {req.status === 'paid' && (
                          <div className="mt-1 bg-emerald-50 border border-emerald-200 rounded p-1.5 text-left">
                            <p className="text-[10px] text-emerald-800 font-semibold">Payment Processed</p>
                            {req.paid_at && <p className="text-[10px] text-emerald-700">Date: {new Date(req.paid_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</p>}
                            {req.paid_by_email && <p className="text-[10px] text-emerald-600">By: {req.paid_by_email}</p>}
                            {req.paid_remarks && <p className="text-[10px] text-emerald-600">Ref: {req.paid_remarks}</p>}
                          </div>
                        )}
                        {req.status === 'rejected' && (
                          <div className="mt-1 bg-red-50 border border-red-200 rounded p-1.5 text-left">
                            <p className="text-[10px] text-red-700">Rejected by {req.rejected_by_email}</p>
                            {req.rejected_at && <p className="text-[10px] text-red-600">on {new Date(req.rejected_at).toLocaleDateString('en-IN')}</p>}
                            {req.rejected_remarks && <p className="text-[10px] text-red-600">Reason: {req.rejected_remarks}</p>}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex flex-wrap justify-center gap-1">
                          {getActionButtons(req).map(btn => {
                            const BtnIcon = btn.icon;
                            return (
                              <Button key={btn.action} size="sm" className={`h-7 text-xs px-2 ${btn.color}`}
                                onClick={() => { setActionDialog({ open: true, req, action: btn.action }); setRemarks(''); }}
                                data-testid={`${btn.action}-req-${req.id}`}
                              >
                                <BtnIcon className="w-3 h-3 mr-1" />{btn.label}
                              </Button>
                            );
                          })}
                          {canDelete && (
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2 text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(req)}
                              data-testid={`delete-req-${req.id}`}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(o) => { if (!o) { setActionDialog({ open: false, req: null, action: '' }); setRemarks(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="capitalize">{actionDialog.action} Requisition</DialogTitle>
          </DialogHeader>
          {actionDialog.req && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p><span className="text-slate-500">Req No:</span> <b>{actionDialog.req.requisition_no}</b></p>
                <p><span className="text-slate-500">Amount:</span> <b>Rs.{actionDialog.req.amount?.toLocaleString('en-IN')}</b></p>
                <p><span className="text-slate-500">Category:</span> {actionDialog.req.category}</p>
                <p><span className="text-slate-500">By:</span> {actionDialog.req.submitted_by_email}</p>
              </div>
              <div className="space-y-2">
                <Label>Remarks (optional)</Label>
                <Input value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Enter remarks..." data-testid="action-remarks" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setActionDialog({ open: false, req: null, action: '' })}>Cancel</Button>
                <Button onClick={handleAction} className={actionDialog.action === 'reject' ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-700 hover:bg-emerald-800 text-white'} data-testid="confirm-action-btn">
                  Confirm {actionDialog.action === 'pay' ? 'Payment' : actionDialog.action}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Requisition Summary Dialog */}
      <Dialog open={summaryDialog.open} onOpenChange={(o) => { if (!o) setSummaryDialog({ open: false, req: null }); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Requisition Summary</DialogTitle>
          </DialogHeader>
          {summaryDialog.req && (() => {
            const r = summaryDialog.req;
            const sc = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
            const StatusIcon = sc.icon;
            return (
              <div className="space-y-3">
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200 text-center">
                  <p className="text-xs text-emerald-600 font-medium">Requisition No.</p>
                  <p className="text-xl font-bold text-emerald-900">{r.requisition_no}</p>
                </div>
                <div className="flex justify-center">
                  <span className={`inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full border font-semibold ${sc.color}`}>
                    <StatusIcon className="w-4 h-4" />{sc.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Date</p><p className="text-sm font-semibold text-slate-900">{new Date(r.date).toLocaleDateString('en-IN', { day:'2-digit', month:'long', year:'numeric' })}</p></div>
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Amount</p><p className="text-sm font-bold text-emerald-800">Rs.{r.amount?.toLocaleString('en-IN')}</p></div>
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Category</p><p className="text-sm font-semibold text-slate-900">{r.category}</p></div>
                  <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Branch</p><p className="text-sm font-semibold text-slate-900">{r.branch_name || '-'}</p></div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Description</p><p className="text-sm text-slate-900">{r.description}</p></div>
                <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-500">Submitted By</p><p className="text-sm text-slate-900">{r.submitted_by_email}</p></div>

                {/* Workflow Trail */}
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs font-semibold text-slate-600 mb-2">Workflow Trail</p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-400"></span><span className="text-slate-600">Submitted by {r.submitted_by_email}</span></div>
                    {r.verified_by_email && <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-500"></span><span className="text-slate-600">Verified by {r.verified_by_email}{r.verified_at ? ` on ${new Date(r.verified_at).toLocaleDateString('en-IN')}` : ''}</span></div>}
                    {r.verified_remarks && <p className="ml-4 text-slate-400 italic">"{r.verified_remarks}"</p>}
                    {r.approved_by_email && <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-purple-500"></span><span className="text-slate-600">Approved by {r.approved_by_email}{r.approved_at ? ` on ${new Date(r.approved_at).toLocaleDateString('en-IN')}` : ''}</span></div>}
                    {r.approved_remarks && <p className="ml-4 text-slate-400 italic">"{r.approved_remarks}"</p>}
                    {r.paid_by_email && <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500"></span><span className="text-emerald-700 font-semibold">Paid by {r.paid_by_email}{r.paid_at ? ` on ${new Date(r.paid_at).toLocaleDateString('en-IN')}` : ''}</span></div>}
                    {r.paid_remarks && <p className="ml-4 text-emerald-600 italic">"{r.paid_remarks}"</p>}
                    {r.rejected_by_email && <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500"></span><span className="text-red-700 font-semibold">Rejected by {r.rejected_by_email}{r.rejected_at ? ` on ${new Date(r.rejected_at).toLocaleDateString('en-IN')}` : ''}</span></div>}
                    {r.rejected_remarks && <p className="ml-4 text-red-600 italic">Reason: "{r.rejected_remarks}"</p>}
                  </div>
                </div>

                {r.has_bill && (
                  <div className="flex justify-center pt-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadBill(r.id, r.bill_filename)} className="text-emerald-700 border-emerald-200">
                      <Download className="w-4 h-4 mr-2" />Download Bill ({r.bill_filename})
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Requisitions;
